import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { camelCaseToWords, sortByAlphabet } from 'shared/utils';
import { colorPalette, MS_PER_DAY } from 'shared/constants';
import { isAvatarsEnabled } from 'shared/store/selectors/features.selector';
import { useProfileLang, useProfileOptions } from 'shared/hooks/profile.hooks';
import { IconButton } from '../design-system';
import { UserAvatar } from '../avatar';
import {
  cellActionKeyboardNavigate,
  SetFilter,
  setFilterDoesFilterPass,
  RadioSetFilter,
  radioSetFilterDoesFilterPass,
  textWithFallback,
  DateCell,
  TranslatedCell,
  UserLinkCell,
} from '../design-system/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-regular-svg-icons';
import styles from './user-list.module.scss';

const COLUMN_WIDTH = 150;

export const ENTITY = {
  USER: 'user',
  CONTACT: 'contact',
};

export const useColumns = ({
  entity = ENTITY.USER,
  renderActions = () => {},
  showLastLogin = false,
  onEditUser = null,
}) => {
  const lang = useSelector(getLang('USERS_MANAGEMENT'));
  const avatarsEnabled = useSelector(isAvatarsEnabled);

  const { userProfileOptions, locationsOptions, getValueFromLang, getLocationsValues } =
    useProfileLang();

  const { fieldHasOptions, fieldLabels } = useProfileOptions({});
  const fieldLabel = useCallback((key, fallback) => fieldLabels?.[key] || fallback, [fieldLabels]);

  const commonColumns = useMemo(
    /**
     * @type {() => import('ag-grid-community').ColDef[]}
     */
    () => [
      {
        headerName: lang.EMAIL,
        field: 'email',
        colId: 'email',
        sortable: true,
        comparator: (a, b) => sortByAlphabet(a, b),
        valueFormatter: textWithFallback,
        flex: 2,
        minWidth: 250,
      },
      ...(showLastLogin
        ? [
            {
              headerName: lang.LAST_SEEN_AT,
              field: 'lastSeenAt',
              colId: 'lastSeenAt',
              sortable: true,
              cellRenderer: DateCell,
              cellRendererParams: { fallback: lang.DID_NOT_SIGN_UP },
              flex: 1,
              minWidth: COLUMN_WIDTH,
              filter: {
                component: RadioSetFilter,
                doesFilterPass: radioSetFilterDoesFilterPass,
              },
              filterParams: {
                options: [
                  {
                    value: 'not_signed_up',
                    label: lang.DID_NOT_SIGN_UP,
                    matcher: v => !v,
                  },
                  {
                    value: '7',
                    label: lang.INACTIVE_7_DAYS,
                    matcher: v => {
                      if (!v) return false;
                      const daysDiff = Math.floor(
                        (Date.now() - new Date(v).getTime()) / MS_PER_DAY,
                      );
                      return daysDiff >= 7;
                    },
                  },
                  {
                    value: '30',
                    label: lang.INACTIVE_30_DAYS,
                    matcher: v => {
                      if (!v) return false;
                      const daysDiff = Math.floor(
                        (Date.now() - new Date(v).getTime()) / MS_PER_DAY,
                      );
                      return daysDiff >= 30;
                    },
                  },
                  {
                    value: '90',
                    label: lang.INACTIVE_90_DAYS,
                    matcher: v => {
                      if (!v) return false;
                      const daysDiff = Math.floor(
                        (Date.now() - new Date(v).getTime()) / MS_PER_DAY,
                      );
                      return daysDiff >= 90;
                    },
                  },
                  {
                    value: '180',
                    label: lang.INACTIVE_180_DAYS,
                    matcher: v => {
                      if (!v) return false;
                      const daysDiff = Math.floor(
                        (Date.now() - new Date(v).getTime()) / MS_PER_DAY,
                      );
                      return daysDiff >= 180;
                    },
                  },
                ],
              },
            },
          ]
        : []),
      {
        headerName: lang.PHONE,
        field: 'phone',
        colId: 'phone',
        sortable: true,
        comparator: (a, b) => sortByAlphabet(a, b),
        valueFormatter: textWithFallback,
        flex: 1,
        minWidth: COLUMN_WIDTH,
      },
      {
        headerName: lang.PAGER,
        field: 'pager',
        colId: 'pager',
        sortable: true,
        comparator: (a, b) => sortByAlphabet(a, b),
        valueFormatter: textWithFallback,
        flex: 1,
        minWidth: COLUMN_WIDTH,
      },
      {
        headerName: lang.CISCO,
        field: 'cisco',
        colId: 'cisco',
        sortable: true,
        comparator: (a, b) => sortByAlphabet(a, b),
        valueFormatter: textWithFallback,
        flex: 1,
        minWidth: COLUMN_WIDTH,
      },
      ...(fieldHasOptions.department
        ? [
            {
              headerName: fieldLabel('department', lang.DEPARTMENT),
              field: 'department',
              colId: 'department',
              sortable: true,
              comparator: (a, b) => sortByAlphabet(a, b),
              cellRenderer: TranslatedCell,
              flex: 1,
              minWidth: COLUMN_WIDTH,
              filter: {
                component: SetFilter,
                doesFilterPass: setFilterDoesFilterPass,
              },
              filterParams: {
                valueFormatter: getValueFromLang,
              },
            },
          ]
        : []),
      ...(fieldHasOptions.profession
        ? [
            {
              headerName: fieldLabel('profession', lang.PROFESSION),
              field: 'profession',
              colId: 'profession',
              sortable: true,
              comparator: (a, b) => sortByAlphabet(a, b),
              cellRenderer: TranslatedCell,
              flex: 1,
              minWidth: COLUMN_WIDTH,
              filter: {
                component: SetFilter,
                doesFilterPass: setFilterDoesFilterPass,
              },
              filterParams: {
                valueFormatter: getValueFromLang,
              },
            },
          ]
        : []),
      ...(fieldHasOptions.role
        ? [
            {
              headerName: fieldLabel('role', lang.ROLE),
              field: 'role',
              colId: 'role',
              sortable: true,
              comparator: (a, b) => sortByAlphabet(a, b),
              cellRenderer: TranslatedCell,
              flex: 1,
              minWidth: COLUMN_WIDTH,
              filter: {
                component: SetFilter,
                doesFilterPass: setFilterDoesFilterPass,
              },
              filterParams: {
                valueFormatter: getValueFromLang,
              },
            },
          ]
        : []),
      ...(fieldHasOptions.levelOfTraining
        ? [
            {
              headerName: fieldLabel('levelOfTraining', lang.LEVEL_OF_TRAINING),
              field: 'levelOfTraining',
              colId: 'levelOfTraining',
              sortable: true,
              comparator: (a, b) => sortByAlphabet(a, b),
              cellRenderer: TranslatedCell,
              flex: 1,
              minWidth: COLUMN_WIDTH,
              filter: {
                component: SetFilter,
                doesFilterPass: setFilterDoesFilterPass,
              },
              filterParams: {
                valueFormatter: getValueFromLang,
              },
            },
          ]
        : []),
      {
        headerName: lang.ROTATION,
        field: 'rotation',
        colId: 'rotation',
        sortable: true,
        comparator: (a, b) => sortByAlphabet(a, b),
        cellRenderer: params => userProfileOptions[params.value] ?? params.value ?? '-',
        flex: 1,
        minWidth: COLUMN_WIDTH,
        filter: {
          component: SetFilter,
          doesFilterPass: setFilterDoesFilterPass,
        },
        filterParams: {
          valueFormatter: v => userProfileOptions[v] ?? v,
        },
      },
      ...(fieldHasOptions.division
        ? [
            {
              headerName: fieldLabel('division', lang.DIVISION),
              field: 'division',
              colId: 'division',
              cellRenderer: TranslatedCell,
              cellRendererParams: { multiple: true },
              flex: 1,
              minWidth: COLUMN_WIDTH,
              filter: {
                component: SetFilter,
                doesFilterPass: setFilterDoesFilterPass,
              },
              filterParams: {
                valueFormatter: getValueFromLang,
              },
            },
          ]
        : []),
      ...(locationsOptions.length > 0
        ? [
            {
              headerName: lang.LOCATION,
              field: 'location',
              colId: 'location',
              cellRenderer: params => getLocationsValues(params.value),
              flex: 1,
              minWidth: COLUMN_WIDTH,
              filter: {
                component: SetFilter,
                doesFilterPass: setFilterDoesFilterPass,
              },
              filterParams: {
                valueFormatter: v => locationsOptions.find(({ code }) => code === v)?.name ?? v,
              },
            },
          ]
        : []),
      ...(fieldHasOptions.specialty
        ? [
            {
              headerName: fieldLabel('specialty', lang.SPECIALTY),
              field: 'specialty',
              colId: 'specialty',
              cellRenderer: TranslatedCell,
              cellRendererParams: { multiple: true },
              flex: 1,
              minWidth: COLUMN_WIDTH,
              filter: {
                component: SetFilter,
                doesFilterPass: setFilterDoesFilterPass,
              },
              filterParams: {
                valueFormatter: getValueFromLang,
              },
            },
          ]
        : []),
      ...(fieldHasOptions.unit
        ? [
            {
              headerName: fieldLabel('unit', lang.UNIT),
              field: 'unit',
              colId: 'unit',
              cellRenderer: TranslatedCell,
              cellRendererParams: { multiple: true },
              flex: 1,
              minWidth: COLUMN_WIDTH,
              filter: {
                component: SetFilter,
                doesFilterPass: setFilterDoesFilterPass,
              },
              filterParams: {
                valueFormatter: getValueFromLang,
              },
            },
          ]
        : []),
      {
        headerName: lang.TITLE,
        field: 'title',
        colId: 'title',
        sortable: true,
        comparator: (a, b) => sortByAlphabet(a, b),
        valueFormatter: textWithFallback,
        flex: 1,
        minWidth: COLUMN_WIDTH,
      },
    ],
    [
      lang,
      userProfileOptions,
      getValueFromLang,
      locationsOptions,
      getLocationsValues,
      showLastLogin,
      fieldHasOptions,
      fieldLabel,
    ],
  );

  const nameColumn = useMemo(
    /**
     * @type {() => import('ag-grid-community').ColDef[]}
     */
    () => [
      {
        headerName: lang.NAME,
        field: 'name',
        colId: 'name',
        sortable: true,
        comparator: (a, b) => sortByAlphabet(a, b),
        valueFormatter: textWithFallback,
        sort: 'asc',
        flex: 1.5,
        minWidth: COLUMN_WIDTH,
      },
    ],
    [lang],
  );

  const userColumns = useMemo(
    /**
     * @type {() => import('ag-grid-community').ColDef[]}
     */
    () => [
      ...(onEditUser
        ? [
            {
              headerName: lang.ACTIONS || 'Actions',
              field: 'actions',
              colId: 'actions',
              cellClass: styles.actions,
              sortable: false,
              cellRenderer: params => (
                <IconButton label={lang.EDIT} onClick={() => onEditUser(params.data)}>
                  <FontAwesomeIcon icon={faPencil} color={colorPalette.primaryMain6} />
                </IconButton>
              ),
              suppressKeyboardEvent: cellActionKeyboardNavigate,
              minWidth: 85,
              maxWidth: 85,
            },
          ]
        : []),
      ...(avatarsEnabled
        ? [
            {
              headerName: lang.PHOTO,
              field: 'avatarPublicId',
              colId: 'avatar',
              cellClass: styles.avatarCell,
              sortable: false,
              cellRenderer: params => (
                <UserAvatar user={params.data} imageSize={128} size="small" />
              ),
              minWidth: 90,
              maxWidth: 90,
            },
          ]
        : []),
      ...nameColumn,
      {
        headerName: lang.PERMISSIONS,
        field: 'permissions',
        colId: 'permissions',
        sortable: true,
        comparator: (a, b) => sortByAlphabet(a, b),
        cellRenderer: params => camelCaseToWords(params.value),
        flex: 1,
        minWidth: COLUMN_WIDTH,
        filter: {
          component: SetFilter,
          doesFilterPass: setFilterDoesFilterPass,
        },
        filterParams: {
          valueFormatter: camelCaseToWords,
        },
      },
      ...commonColumns,
      {
        headerName: lang.DEACTIVATE_AT,
        field: 'deactivationDate',
        colId: 'deactivationDate',
        cellRenderer: DateCell,
        flex: 1,
        minWidth: COLUMN_WIDTH,
      },
    ],
    [lang, commonColumns, nameColumn, onEditUser, avatarsEnabled],
  );

  const contactColumns = useMemo(
    /**
     * @type {() => import('ag-grid-community').ColDef[]}
     */
    () => [
      {
        headerName: lang.ACTIONS,
        field: 'actions',
        colId: 'actions',
        cellClass: styles.actions,
        cellRenderer: params => renderActions(params.data),
        suppressKeyboardEvent: cellActionKeyboardNavigate,
        minWidth: 100,
        maxWidth: 100,
        sortable: false,
      },
      {
        headerName: lang.NAME,
        field: 'name',
        colId: 'name',
        sortable: true,
        comparator: (a, b) => sortByAlphabet(a, b),
        sort: 'asc',
        flex: 1,
        minWidth: COLUMN_WIDTH,
        cellRenderer: UserLinkCell,
        cellRendererParams: { className: styles.nameCell },
        suppressKeyboardEvent: cellActionKeyboardNavigate,
      },
      ...commonColumns,
    ],
    [lang, commonColumns, renderActions],
  );

  if (entity === ENTITY.USER) {
    return userColumns;
  }

  if (entity === ENTITY.CONTACT) {
    return contactColumns;
  }
  return null;
};
